import React, { useState } from 'react';
import { CheckCircle2, ArrowRight, Flower, Sprout, Trees } from 'lucide-react';
import PaymentModal from '../ui/PaymentModal';

const ProductShowcaseSection: React.FC = () => {
  const [isBloomModalOpen, setIsBloomModalOpen] = useState(false);
  const [isThriveModalOpen, setIsThriveModalOpen] = useState(false);
  const [isFlourishModalOpen, setIsFlourishModalOpen] = useState(false);

  // Payment URLs for packages
  const bloomPaymentUrl = "https://link.fastpaydirect.com/payment-link/68a3592d67ee3b5bcc685385";
  const thrivePaymentUrl = "https://link.fastpaydirect.com/payment-link/68a3983b613b1b4359cd3807";
  const flourishPaymentUrl = "https://link.fastpaydirect.com/payment-link/68a39a89cd9d344b9b362523";

  const handleBloomCTAClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsBloomModalOpen(true);
  };

  const handleThriveCTAClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsThriveModalOpen(true);
  };

  const handleFlourishCTAClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsFlourishModalOpen(true);
  };
  const products = [
    {
      title: 'BLOOM',
      icon: <Sprout className="h-10 w-10 text-white" />,
      description: (
        <div className="space-y-3">
          <p className="font-semibold text-[#2a2b2a]">BLOOM – When you're unsure where to start…</p>
          <p><span className="font-semibold">The Problem:</span> You know you need a stronger digital presence, but between websites, funnels, ads, and automation—it feels overwhelming. You're not sure what's most important, or how to do it without wasting money.</p>
          <p><span className="font-semibold">The Solution:</span> BLOOM gives you a clear blueprint. We'll help you optimize your website, set up your CRM, create your first funnel, and run starter ads that begin attracting leads right away.</p>
          <p><span className="font-semibold">The Success:</span> Instead of spinning your wheels, you'll have a professional digital foundation and confidence that your business is moving in the right direction.</p>
          <p className="text-[#bb141a]">👉 Perfect for startups and small businesses ready to get traction.</p>
        </div>
      ),
      features: [],
      price: '$1200',
      period: ' a month',
      pricePrefix: 'starting at ',
      ctaText: 'Get Started',
      ctaHref: '#',
      isBloomPackage: true,
      color: '#bb141a'
    },
    {
      title: 'THRIVE',
      icon: <Flower className="h-10 w-10 text-white" />,
      description: (
        <div className="space-y-3">
          <p className="font-semibold text-[#2a2b2a]">THRIVE – When you're stuck doing everything manually…</p>
          <p><span className="font-semibold">The Problem:</span> Your business is growing, but your systems aren't keeping up. Leads fall through the cracks. Your CRM is a mess. You're juggling campaigns that don't talk to each other. Scaling feels messy and exhausting.</p>
          <p><span className="font-semibold">The Solution:</span> THRIVE connects everything. We integrate your CRM with automation tools, build smart workflows, and run ad campaigns that work together. We also give you monthly growth strategy sessions and dashboards so you know exactly what's working.</p>
          <p><span className="font-semibold">The Success:</span> You'll stop wasting time on manual tasks and start scaling with systems that run in the background, giving you more time and better results.</p>
          <p className="text-[#bb141a]">👉 Perfect for growing companies who need data and automation to expand sustainably.</p>
        </div>
      ),
      features: [],
      price: '$2500',
      period: ' a month',
      pricePrefix: 'starting at ',
      ctaText: 'Get Started',
      ctaHref: '#',
      isThrivePackage: true,
      color: '#2a2b2a'
    },
    {
      title: 'FLOURISH',
      icon: <Trees className="h-10 w-10 text-white" />,
      description: (
        <div className="space-y-3">
          <p className="font-semibold text-[#2a2b2a]">FLOURISH – When you’re ready to play at the highest level…</p>
          <p><span className="font-semibold">The Problem:</span> You’ve scaled successfully, but you want more than incremental growth. Competitors are catching up, the market is shifting, and you need a smarter way to win.</p>
          <p><span className="font-semibold">The Solution:</span> FLOURISH transforms your marketing with AI. From private GPT tools to advanced data analysis, from high-performance ad campaigns to compliance-ready AI infrastructure—we help you unlock growth that your competitors can’t touch.</p>
          <p><span className="font-semibold">The Success:</span> You’ll have clarity, precision, and an AI-enhanced growth engine that puts you ahead of the curve—and keeps you there.</p>
          <p className="text-[#bb141a]">👉 Perfect for high-growth businesses who want AI-powered marketing transformation.</p>
        </div>
      ),
      features: [],
      price: '$5000',
      period: ' a month',
      pricePrefix: 'starting at ',
      ctaText: 'Get Started',
      ctaHref: '#',
      isFlourishPackage: true,
      color: '#f59d40'
    }
  ];

  return (
    <section id="product-showcase" className="py-16 bg-gray-50 relative">
      {/* Simple, professional background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white to-gray-100"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#2a2b2a]">
            AI-Powered <span className="text-[#bb141a]">Bundles</span> for Business Growth
          </h2>
          <p className="text-[#2a2b2a] max-w-3xl mx-auto text-lg">
            Discover our expertly bundled services designed to simplify your marketing, automation, and AI strategy. Each package is crafted to maximize efficiency, streamline workflows, and drive real results—all at a transparent price.
          </p>
        </div>
        
        <div className="space-y-16">
          {products.map((product, index) => (
            <div 
              key={index} 
              className={`flex flex-col md:flex-row ${index % 2 === 1 ? 'md:flex-row-reverse' : ''} gap-8 relative bg-white rounded-lg shadow-md overflow-hidden`}
            >
              {/* Left side with icon and description */}
              <div className="md:w-1/2 p-8 flex flex-col justify-center">
                <div className="flex items-center mb-4">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mr-4"
                    style={{ backgroundColor: product.color }}
                  >
                    {product.icon}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#2a2b2a]">{product.title}</h3>
                </div>
                <div className="text-lg text-gray-600 mb-6 border-l-2 pl-4" style={{ borderColor: product.color }}>{product.description}</div>
                {product.features && product.features.length > 0 && (
                  <ul className="grid grid-cols-1 gap-3 mb-8">
                    {product.features.map((feature, i) => (
                      <li key={i} className="flex items-center">
                        <CheckCircle2 className="h-5 w-5 mr-3 flex-shrink-0" style={{ color: product.color }} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
              {/* Right side with pricing */}
              <div className="md:w-1/2 p-8 flex flex-col justify-center bg-gray-50">
                <div className="bg-white rounded-lg p-8 shadow-sm text-center">
                  <h4 className="text-xl font-semibold mb-2">Pricing</h4>
                  <div className="mb-6">
                    <span className="text-gray-600">{product.pricePrefix}</span>
                    <span className="text-3xl font-bold" style={{ color: product.color }}>{product.price}</span>
                    <span className="text-gray-600">{product.period}</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-6">Includes all features with full support</p>
                  {(product as any).isBloomPackage ? (
                    <button
                      onClick={handleBloomCTAClick}
                      className="inline-flex items-center justify-center w-full px-6 py-3 rounded-md text-white font-medium transition-colors hover:opacity-90"
                      style={{
                        backgroundColor: product.color
                      }}
                    >
                      {product.ctaText} <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                  ) : (product as any).isThrivePackage ? (
                    <button
                      onClick={handleThriveCTAClick}
                      className="inline-flex items-center justify-center w-full px-6 py-3 rounded-md text-white font-medium transition-colors hover:opacity-90"
                      style={{
                        backgroundColor: product.color
                      }}
                    >
                      {product.ctaText} <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                  ) : (product as any).isFlourishPackage ? (
                    <button
                      onClick={handleFlourishCTAClick}
                      className="inline-flex items-center justify-center w-full px-6 py-3 rounded-md text-white font-medium transition-colors hover:opacity-90"
                      style={{
                        backgroundColor: product.color
                      }}
                    >
                      {product.ctaText} <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                  ) : (
                    <a
                      href={product.ctaHref}
                      className="inline-flex items-center justify-center w-full px-6 py-3 rounded-md text-white font-medium transition-colors hover:opacity-90"
                      style={{
                        backgroundColor: product.color
                      }}
                    >
                      {product.ctaText} <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <PaymentModal
        isOpen={isBloomModalOpen}
        onClose={() => setIsBloomModalOpen(false)}
        paymentUrl={bloomPaymentUrl}
        title="Bloom Package - Complete Your Purchase"
      />
      
      <PaymentModal
        isOpen={isThriveModalOpen}
        onClose={() => setIsThriveModalOpen(false)}
        paymentUrl={thrivePaymentUrl}
        title="Thrive Package - Complete Your Purchase"
      />
      
      <PaymentModal
        isOpen={isFlourishModalOpen}
        onClose={() => setIsFlourishModalOpen(false)}
        paymentUrl={flourishPaymentUrl}
        title="Flourish Package - Complete Your Purchase"
      />
    </section>
  );
};

export default ProductShowcaseSection;